import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';

import Text from '@lightspeed/cirrus-text';
import Bone from '@lightspeed/cirrus-bone';

const baseClass = 'cr-table';

const TABLE_TYPES = {
  NORMAL: 'normal',
  COMPACT: 'compact',
};

const TABLE_CELL_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
  JUSTIFY: 'justify',
};

const textAlignClass = (align = 'left') => `cr-text-${align}`;

const getNoDataBody = ({ head, noDataMessage }) => {
  const { cells } = head;
  return [
    {
      key: 'no-data',
      cells: [
        {
          content: noDataMessage ? (
            <Text body dimmed className="cr-text-center cr-p-5">
              {noDataMessage}
            </Text>
          ) : (
            ''
          ),
          colSpan: cells.filter(({ hide }) => !hide).length,
        },
      ],
      className: 'cr-table__body-row--no-data',
    },
  ];
};

// eslint-disable-next-line react/prop-types
const renderSkeletonHeader = ({ head, tableType }) => {
  const { hide, cells } = head;
  return (
    <tr className={cn(`${baseClass}__head-row`, { 'cr-display-none': !!hide })}>
      {cells.map(cell => {
        const { key, width } = cell;
        const style = width && { width };
        const headerCellCls = cn(`${baseClass}__header-cell`, {
          [`${baseClass}__header-cell--${tableType}`]: tableType,
        });
        return (
          <th key={key} className={headerCellCls} style={style}>
            <Bone />
          </th>
        );
      })}
    </tr>
  );
};

const renderSkeletonBody = ({ head, tableType }) => {
  const skeletonRows = Array.from(Array(5).keys());
  const { cells } = head;
  return skeletonRows.map((skeletonRowKey, skeletonRowIndex) => (
    <tr className={cn(`${baseClass}__body-row`)} key={skeletonRowKey}>
      {cells.map((cell, index) => {
        const { key } = cell;
        const columnWidth = get(head, `cells.${index}.width`);
        const style = columnWidth && { width: columnWidth };
        const bodyCellCls = cn(`${baseClass}__body-cell`, {
          [`${baseClass}__body-cell--first`]: skeletonRowIndex === 0,
          [`${baseClass}__body-cell--${tableType}`]: tableType,
        });
        return (
          <th key={key} style={style} className={bodyCellCls}>
            <Bone />
          </th>
        );
      })}
    </tr>
  ));
};

// eslint-disable-next-line react/prop-types
const renderHeader = ({ head, tableType }) => {
  const { hide, cells, className, ...rest } = head;
  return (
    <tr
      className={cn(`${baseClass}__head-row`, className, { 'cr-display-none': !!hide })}
      {...rest}
    >
      {cells.map(cell => {
        const {
          key,
          content,
          align,
          width,
          hide: hideCell,
          className: cellClassName,
          style,
          ...cellRest
        } = cell;
        const cellStyle = width ? { ...style, width } : style;
        const headerCellCls = cn(
          `${baseClass}__header-cell`,
          textAlignClass(align),
          cellClassName,
          {
            [`${baseClass}__header-cell--${tableType}`]: tableType,
          }
        );
        return (
          !hideCell && (
            <th key={key} className={headerCellCls} style={cellStyle} {...cellRest}>
              {content}
            </th>
          )
        );
      })}
    </tr>
  );
};

const renderBody = ({ head, body, tableType, noDataMessage }) => {
  const bodyToRender = !body.length ? getNoDataBody({ head, noDataMessage }) : body;
  return bodyToRender.map((row, rowIndex) => {
    const {
      key: rowKey,
      className: rowClassName,
      cells: rowCells,
      expandable,
      expanded,
      error,
      hover,
      ...rowRest
    } = row;
    const bodyRowCls = cn(`${baseClass}__body-row`, rowClassName, {
      [`${baseClass}__body-row--hover`]: hover,
      [`${baseClass}__body-row--expandable`]: expandable,
      [`${baseClass}__body-row--expanded`]: expanded,
      [`${baseClass}__body-row--error`]: error,
    });
    return (
      <tr key={rowKey} className={bodyRowCls} {...rowRest}>
        {rowCells.map((cell, cellIndex) => {
          const {
            key: cellKey,
            content: cellContent,
            className: cellClassName,
            style,
            ...cellRest
          } = cell;
          const columnWidth = get(head, `cells.${cellIndex}.width`);
          const cellStyle = columnWidth ? { width: columnWidth, ...style } : style;
          const bodyCellCls = cn(
            `${baseClass}__body-cell`,
            textAlignClass(get(head, `cells.${cellIndex}.align`)),
            cellClassName,
            {
              [`${baseClass}__body-cell--first`]: rowIndex === 0,
              [`${baseClass}__body-cell--${tableType}`]: tableType,
            }
          );
          return (
            !get(head, `cells.${cellIndex}.hide`, false) && (
              <td
                key={cellKey || `${get(head, `cells.${cellIndex}.key`)}-${rowKey}`}
                style={cellStyle}
                className={bodyCellCls}
                {...cellRest}
              >
                {cellContent}
              </td>
            )
          );
        })}
      </tr>
    );
  });
};

// eslint-disable-next-line react/prop-types
const renderLoadingOverlay = ({ headerColumnsCount, isLoading }) => (
  <tr className={cn(`${baseClass}__body-row`, `${baseClass}__body-row--loading`)}>
    <td
      className={cn(`${baseClass}__body-cell`, `${baseClass}__body-cell--loading`)}
      colSpan={headerColumnsCount}
    >
      <div className={cn('cr-table__loading', { 'cr-table__loading--hidden': !isLoading })}>
        <div
          className={cn('cr-table__loading-progress', {
            'cr-table__loading-progress--start': isLoading,
            'cr-table__loading-progress--complete': !isLoading,
          })}
        />
      </div>
    </td>
  </tr>
);

const Table = ({ type, head, body, isLoading, dataInit, noDataMessage, ...rest }) => {
  const { className: tableClassName, debug, ...tableRest } = rest;
  const { cells = [] } = head;
  const dataReceived = dataInit || !!body.length;
  return cells.length ? (
    <table
      className={cn(baseClass, tableClassName, `${baseClass}--${type}`, {
        [`${baseClass}--debug`]: debug,
      })}
      {...tableRest}
    >
      <thead>
        {!dataReceived && isLoading && renderSkeletonHeader({ head, tableType: type })}
        {dataReceived && renderHeader({ head })}
      </thead>
      <tbody>
        {!dataReceived && isLoading && renderSkeletonBody({ head, tableType: type })}
        {dataReceived && renderBody({ head, body, tableType: type, noDataMessage })}
        {dataReceived &&
          renderLoadingOverlay({
            headerColumnsCount: cells.filter(({ hide }) => !hide).length,
            isLoading,
          })}
      </tbody>
    </table>
  ) : null;
};

Table.propTypes = {
  type: PropTypes.oneOf([...Object.values(TABLE_TYPES)]),
  head: PropTypes.shape({
    hide: PropTypes.bool,
    cells: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        hide: PropTypes.bool,
        content: PropTypes.node,
        align: PropTypes.oneOf([...Object.values(TABLE_CELL_ALIGN)]),
        width: PropTypes.string,
      })
    ),
  }),
  body: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      hover: PropTypes.bool,
      selected: PropTypes.bool,
      error: PropTypes.bool,
      cells: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          content: PropTypes.node,
        })
      ),
    })
  ),
  isLoading: PropTypes.bool,
  dataInit: PropTypes.bool,
  noDataMessage: PropTypes.string,
  debug: PropTypes.bool,
};

Table.defaultProps = {
  type: TABLE_TYPES.NORMAL,
  head: { cells: [], hide: false },
  body: [],
};

export default Table;

export { TABLE_TYPES, TABLE_CELL_ALIGN };
